/**
 * Identidad de invitado: nombre y avatar de quien juega sin cuenta.
 *
 * Vive en `sessionStorage`, no en `localStorage`, y la diferencia es el punto
 * entero de este módulo: lo que se guarda aquí desaparece al cerrar el
 * navegador, igual que en Kahoot. Un aula o un ordenador compartido no arrastra
 * al siguiente el apodo y la cara del anterior.
 *
 * Dentro de la misma pestaña sí sobrevive a una recarga, que es lo que hace
 * falta para que perder la conexión a mitad de partida no borre quién eras.
 *
 * Nada de esto es identidad de verdad: el servidor asigna a cada invitado un id
 * nuevo en cada conexión y no le atribuye progreso. Es solo comodidad visual.
 * La excepción es crear una cuenta al terminar: entonces las partidas de esta
 * visita pasan a ella (ver `claimPendingGuestResult`).
 */

import type { AvatarConfig } from '../types';
import { randomAvatar } from '../components/Avatar';
import { api, type Profile } from './api';

const KEY = 'open_dahu_guest';

export interface GuestIdentity {
  name: string;
  avatar: AvatarConfig;
  /**
   * Identidad que el servidor asignó a esta visita, y el testigo que prueba que
   * es nuestra. Se reenvían al reconectar para recuperar el mismo jugador en
   * la sala en vez de aparecer como uno nuevo.
   */
  id?: string;
  token?: string;
}

export function readGuest(): GuestIdentity | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<GuestIdentity>;
    if (!parsed || typeof parsed.name !== 'string' || !parsed.avatar) return null;
    return {
      name: parsed.name,
      avatar: parsed.avatar as AvatarConfig,
      id: typeof parsed.id === 'string' ? parsed.id : undefined,
      token: typeof parsed.token === 'string' ? parsed.token : undefined,
    };
  } catch {
    // Modo privado, almacenamiento bloqueado o JSON corrupto: se empieza de cero.
    return null;
  }
}

export function saveGuest(identity: GuestIdentity): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(identity));
  } catch {
    /* sin almacenamiento, la identidad dura lo que dure la pestaña en memoria */
  }
}

/** Guarda la identidad que devolvió el servidor, conservando apodo y avatar. */
export function saveGuestCredentials(id: string, token: string): void {
  const actual = readGuest();
  saveGuest({
    name: actual?.name ?? '',
    avatar: actual?.avatar ?? randomAvatar(),
    id,
    token,
  });
}

export function clearGuest(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    /* nada que limpiar */
  }
}

/** Identidad de partida: la de esta sesión si existe, o una recién generada. */
export function guestOrNew(): GuestIdentity {
  return readGuest() ?? { name: '', avatar: randomAvatar() };
}

/* ------------------------------------------------------------------ *
 * Guardar el resultado al crear cuenta
 *
 * Al terminar una partida sin cuenta se ofrece guardar el resultado. Si la
 * persona acepta, se marca aquí y, en cuanto hay sesión, las partidas de esta
 * visita pasan a la cuenta. La marca vive en `sessionStorage` y no en memoria
 * porque entrar con Google recarga la página.
 * ------------------------------------------------------------------ */

const CLAIM_KEY = 'open_dahu_claim_guest';

export function markPendingClaim(pending: boolean): void {
  try {
    if (pending) sessionStorage.setItem(CLAIM_KEY, '1');
    else sessionStorage.removeItem(CLAIM_KEY);
  } catch {
    /* sin almacenamiento no hay nada que recordar */
  }
}

function hasPendingClaim(): boolean {
  try {
    return sessionStorage.getItem(CLAIM_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Pasa a la cuenta recién abierta las partidas jugadas como invitado, si se
 * pidió. Hay que llamarla ANTES de guardar el perfil en el estado: al cambiar
 * de identidad, la app borra las credenciales de invitado.
 *
 * Devuelve el perfil actualizado si se guardó algo, o `null` si no.
 */
export async function claimPendingGuestResult(): Promise<Profile | null> {
  if (!hasPendingClaim()) return null;
  markPendingClaim(false);
  const guest = readGuest();
  if (!guest?.id || !guest.token) return null;
  try {
    const data = await api.post<{ claimed: number; profile: Profile | null }>(
      '/profile/claim-guest',
      { guestId: guest.id, guestToken: guest.token },
    );
    return data.claimed > 0 ? data.profile : null;
  } catch {
    // Un fallo aquí no debe impedir entrar a la cuenta recién creada.
    return null;
  }
}
